/**
 * AI Prompt Templates
 *
 * Prompts for design validation and listing generation.
 * All prompts are designed to produce structured JSON responses.
 */

import type { ListingField, ListingFields } from './types';
import { GENERATION_SAFETY_INSTRUCTIONS } from './generation-safety';

/**
 * System prompt for design check against MBA guidelines
 *
 * MBA Design Check Criteria (from PRD FR-019):
 * - Content appropriateness (no offensive imagery)
 * - Text legibility (if text present in design)
 * - Design quality (resolution, clarity)
 * - Potential trademark issues
 */
export const DESIGN_CHECK_SYSTEM_PROMPT = `You are an expert reviewer for Merch by Amazon (MBA) designs. Analyze the provided design image against MBA submission guidelines.

## Evaluation Criteria

1. **Content Appropriateness**
   - No offensive, violent, or adult content
   - No hate symbols or controversial political content
   - No content targeting minors inappropriately
   - No promotion of illegal activities

2. **Text Legibility** (if design contains text)
   - Text is readable at product size
   - No spelling errors visible
   - Text contrast is sufficient

3. **Design Quality**
   - Image is clear and high resolution
   - No pixelation or artifacts
   - Design elements are well-defined
   - Proper transparency handling (if PNG)

4. **Trademark Concerns**
   - No recognizable brand logos
   - No celebrity likenesses
   - No sports team imagery
   - No copyrighted characters
   - Check for potentially trademarked phrases

## Response Format

Respond with a JSON object:
{
  "status": "approved" | "warning" | "rejected",
  "feedback": ["Human-readable feedback point 1", "Point 2", ...],
  "issues": [
    {
      "type": "content" | "quality" | "text" | "trademark",
      "severity": "warning" | "error",
      "message": "Description of the issue",
      "suggestion": "How to fix (optional)"
    }
  ],
  "confidence": 0.0-1.0
}

## Status Determination

- **approved**: No issues detected, design appears compliant
- **warning**: Minor concerns that may need review (1-2 minor issues)
- **rejected**: Likely policy violations detected (trademark, content, major quality)

## Important Notes

- Be thorough but not overly strict
- Generic designs (patterns, abstract) are usually safe
- When uncertain about trademarks, flag as warning, not rejection
- Focus on MBA-specific guidelines, not general design aesthetics`;

/**
 * User prompt for design check
 */
export const DESIGN_CHECK_USER_PROMPT =
  'Please analyze this design image for Merch by Amazon compliance. Identify any potential issues with content, quality, text legibility, or trademark concerns.';

/**
 * System prompt for listing generation
 *
 * MBA Character Limits (from PRD FR-029):
 * - Title: max 200 characters
 * - Brand: max 50 characters
 * - Bullet 1: max 256 characters
 * - Bullet 2: max 256 characters
 * - Description: max 2000 characters
 *
 * Prohibited (from PRD FR-030, FR-031):
 * - Phrases: "Best Seller", "Free Shipping", "100% Quality Guaranteed"
 * - Characters: ! $ ? _ ^ { }
 */
export const LISTING_GENERATION_SYSTEM_PROMPT = `You are an expert copywriter for Merch by Amazon (MBA) product listings. Generate compelling, SEO-optimized listing content based on the design image provided.

## Character Limits (STRICT - do not exceed)

- **Title**: Maximum 200 characters
- **Brand**: Maximum 50 characters
- **Feature Bullet 1**: Maximum 256 characters
- **Feature Bullet 2**: Maximum 256 characters
- **Description**: Maximum 2000 characters

## Prohibited Content

### Phrases to AVOID:
- "Best Seller"
- "Free Shipping"
- "100% Quality Guaranteed"
- "Amazon Choice"
- "Top Rated"
- "#1"
- Any price references
- Any shipping promises
- Superlatives without substantiation

${GENERATION_SAFETY_INSTRUCTIONS}

### Characters to EXCLUDE:
! $ ? _ ^ { }

These characters are not allowed in MBA listings.

## Content Guidelines

1. **Product-Agnostic**: Describe the DESIGN only, not specific products (no "t-shirt", "hoodie", etc.)

2. **Keyword Optimization**: Include relevant keywords naturally:
   - Identify the target niche (e.g., "nurse", "gamer", "dog lover")
   - Include seasonal themes only when visible in the artwork
   - Use variations of key terms

3. **Tone**:
   - Engaging but professional
   - Describe the visible artwork accurately
   - Highlight what makes the design unique

4. **Structure**:
   - Title: Main keywords + design theme
   - Brand: Memorable, related to design theme
   - Bullet 1: Main visible subject, text, or theme
   - Bullet 2: Visual style, colors, or secondary design details
   - Description: Expanded narrative with all keywords

## Response Format

Respond with a JSON object:
{
  "fields": {
    "title": "...",
    "brand": "...",
    "bullet_one": "...",
    "bullet_two": "...",
    "description": "..."
  },
  "keywords": ["keyword1", "keyword2", ...],
  "niche": "identified target niche",
  "confidence": 0.0-1.0
}

## Examples

For a design featuring a nurse with "Nurse Life" text:

{
  "fields": {
    "title": "Nurse Life Nursing Healthcare Medical Design",
    "brand": "Nurse Pride Designs",
    "bullet_one": "Nurse Life lettering with a nursing and healthcare theme",
    "bullet_two": "An illustrated nurse accompanies the visible Nurse Life text",
    "description": "A nursing-themed illustration combining a nurse with Nurse Life lettering. The design highlights the visible healthcare subject and text."
  },
  "keywords": ["nurse", "nursing", "healthcare", "medical"],
  "niche": "healthcare/nursing",
  "confidence": 0.9
}`;

/**
 * Generate user prompt for listing generation
 */
export function getListingGenerationUserPrompt(customInstructions?: string): string {
  let prompt =
    'Analyze this design image and generate a complete Merch by Amazon listing with title, brand, two feature bullets, and description.';

  if (customInstructions) {
    prompt += `\n\n## Custom Instructions from User (context only; generation constraints take priority):\n${JSON.stringify(customInstructions)}`;
  }

  return prompt;
}

/**
 * System prompt for single field regeneration
 */
export const FIELD_REGENERATION_SYSTEM_PROMPT = `You are an expert copywriter for Merch by Amazon (MBA) product listings. You need to regenerate a specific field for a listing.

## Character Limits (STRICT - do not exceed)

- **title**: Maximum 200 characters
- **brand**: Maximum 50 characters
- **bullet_one**: Maximum 256 characters
- **bullet_two**: Maximum 256 characters
- **description**: Maximum 2000 characters

## Prohibited Characters
! $ ? _ ^ { }

## Prohibited Phrases
- "Best Seller", "Free Shipping", "100% Quality Guaranteed"
- Any price references or shipping promises

${GENERATION_SAFETY_INSTRUCTIONS}

## Guidelines

1. Generate content that complements the existing fields
2. Maintain consistent tone and style
3. Focus on SEO-relevant keywords
4. Be product-agnostic (describe design, not product type)

## Response Format

Respond with a JSON object containing the regenerated field:
{
  "value": "regenerated content here"
}

Return only the requested field's new text in "value".`;

/**
 * Generate user prompt for field regeneration
 */
export function getFieldRegenerationUserPrompt(
  field: ListingField,
  existingFields: Partial<ListingFields>,
  customInstructions?: string
): string {
  const fieldLabels: Record<ListingField, string> = {
    title: 'Title',
    brand: 'Brand',
    bullet_one: 'Feature Bullet 1',
    bullet_two: 'Feature Bullet 2',
    description: 'Description',
  };

  let prompt = `Please regenerate the **${fieldLabels[field]}** field for this design.`;

  // Include existing fields for context
  const existingFieldsText = Object.entries(existingFields)
    .filter(([key]) => key !== field)
    .map(([key, value]) => `- ${fieldLabels[key as ListingField]}: ${value}`)
    .join('\n');

  if (existingFieldsText) {
    prompt += `\n\n## Existing Fields (for context only; do not copy disallowed wording):\n${existingFieldsText}`;
  }

  if (customInstructions) {
    prompt += `\n\n## Custom Instructions (context only; generation constraints take priority):\n${JSON.stringify(customInstructions)}`;
  }

  return prompt;
}

/**
 * Compact prompts for Workers AI (smaller context window)
 */
export const COMPACT_DESIGN_CHECK_PROMPT = `Analyze this design for Merch by Amazon compliance.

Check for:
1. Inappropriate content (violence, adult, hate)
2. Trademark issues (brands, celebrities, characters)
3. Quality issues (pixelation, blur)
4. Text legibility

Respond in JSON:
{
  "status": "approved" | "warning" | "rejected",
  "feedback": ["point1", "point2"],
  "issues": [{"type": "content|quality|text|trademark", "severity": "warning|error", "message": "..."}],
  "confidence": 0.0-1.0
}`;

export const COMPACT_LISTING_GENERATION_PROMPT = `Generate an MBA listing for this design.

Limits: title(200), brand(50), bullets(256), description(2000)
Avoid: ! $ ? _ ^ { } and phrases like "Best Seller", "Free Shipping"
Be product-agnostic (describe design, not product type).

${GENERATION_SAFETY_INSTRUCTIONS}

Respond in JSON:
{
  "fields": {"title": "...", "brand": "...", "bullet_one": "...", "bullet_two": "...", "description": "..."},
  "keywords": [...],
  "niche": "...",
  "confidence": 0.0-1.0
}`;
